import React, { useEffect, useState } from "react";
import "./navbar.component.scss";
import { Box, Modal, Stack, Text } from "@mantine/core";

import logo from "../../assets/coachsafely-navbar-logo.png";
import bgImg from "../../assets/navbar-coachsafely-hero.png";
import UserDiv from "../common/userdiv/UserDiv";
import { AskHelp } from "../ask-help/ask-help.component";
import { CertificateDownload } from "../certificate-download/certificate-download.component";
import { useSelector } from "react-redux";
import navigationService from "../../services/navigation/navigation.service";
import courseComander from "../../store/commanders/course.comander";
import applicationComander from "../../store/commanders/application.comander";
import PLButton from "../common/button";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "../localization/language.switcher";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

const InstallIcon = ({ type }: { type: "menu" | "share" | "home" | "install" }) => {
  switch (type) {
    case "menu":
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M4 7h16M4 12h16M4 17h16"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
        </svg>
      );
    case "share":
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M12 16V5M12 5l-4 4M12 5l4 4M5 14v4a1 1 0 001 1h12a1 1 0 001-1v-4"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
        </svg>
      );
    case "home":
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M4 11.5L12 5l8 6.5V19a1 1 0 01-1 1h-4.5v-5h-5v5H5a1 1 0 01-1-1v-7.5z"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
        </svg>
      );
    case "install":
    default:
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M12 3v10m0 0l-4-4m4 4l4-4M5 15v3a1 1 0 001 1h12a1 1 0 001-1v-3"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
        </svg>
      );
  }
};

const NavbarComponent = ({ user, userData }: { user: any; userData: any }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const course = useSelector((state: any) => state.course);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [installPromptEvent, setInstallPromptEvent] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const [isMobileInstallContext, setIsMobileInstallContext] = useState(false);
  const [isIosSafari, setIsIosSafari] = useState(false);
  const [isAndroidChrome, setIsAndroidChrome] = useState(false);
  const showMobilePageLinks =
    !!user?.email &&
    ["/account", "/course", "/background"].includes(location.pathname);
  const showDesktopPageLinks =
    !!user?.email &&
    ["/account", "/course", "/background"].includes(location.pathname);
  const accountDisabled =
    !userData?.enrolled ||
    (!userData?.valid_subscription && userData?.state !== "AL");

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const updateDesktopHeroHeight = () => {
      const root = document.documentElement;
      const viewportHeight = window.innerHeight;
      const minimumRouteContentHeight = 540;
      const heroHeight = Math.max(
        88,
        Math.min(
          180,
          viewportHeight - minimumRouteContentHeight - 65,
        ),
      );

      root.style.setProperty("--desktop-navbar-hero-height", `${heroHeight}px`);
    };

    const updateInstallEnvironment = () => {
      const userAgent = window.navigator.userAgent || "";
      const isTouchDevice =
        window.matchMedia("(pointer: coarse)").matches ||
        window.navigator.maxTouchPoints > 0 ||
        "ontouchstart" in window;
      const isSmallViewport = window.matchMedia("(max-width: 1024px)").matches;
      const mobileDevice = isTouchDevice || isSmallViewport;
      const isiOSDevice = /iPhone|iPad|iPod/i.test(userAgent);
      const isSafariBrowser =
        /Safari/i.test(userAgent) && !/CriOS|Chrome|FxiOS|EdgiOS/i.test(userAgent);
      const isAndroidDevice = /Android/i.test(userAgent);
      const isChromeBrowser = /Chrome|CriOS/i.test(userAgent);

      setIsMobileInstallContext(mobileDevice);
      setIsIosSafari(isiOSDevice && isSafariBrowser);
      setIsAndroidChrome(isAndroidDevice && isChromeBrowser);
    };

    const checkInstalled = () => {
      const isStandaloneDisplayMode = window.matchMedia(
        "(display-mode: standalone)",
      ).matches;
      const isNavigatorStandalone =
        "standalone" in window.navigator &&
        (window.navigator as Navigator & { standalone?: boolean }).standalone ===
          true;

      setIsInstalled(isStandaloneDisplayMode || isNavigatorStandalone);
    };

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPromptEvent(event as BeforeInstallPromptEvent);
      checkInstalled();
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPromptEvent(null);
    };

    updateDesktopHeroHeight();
    updateInstallEnvironment();
    checkInstalled();

    window.addEventListener(
      "beforeinstallprompt",
      handleBeforeInstallPrompt as EventListener,
    );
    window.addEventListener("appinstalled", handleAppInstalled);
    window.addEventListener("resize", updateInstallEnvironment);
    window.addEventListener("resize", updateDesktopHeroHeight);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt as EventListener,
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
      window.removeEventListener("resize", updateInstallEnvironment);
      window.removeEventListener("resize", updateDesktopHeroHeight);
    };
  }, []);

  const recertify = () => {
    if (userData.state === "AL") {
      courseComander.cmd_recertify_AL();
    } else {
      navigationService.navigate("/payment", { recertify: true });
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const goToCourseSection = (path: "/course" | "/background") => {
    closeMobileMenu();
    navigationService.navigate(path);
  };

  const goToAccount = () => {
    closeMobileMenu();
    navigationService.navigate("/account");
  };

  const signOut = () => {
    closeMobileMenu();
    applicationComander.cmd_signOut();
  };

  const installApp = async () => {
    if (!installPromptEvent) {
      setShowInstallHelp(true);
      return;
    }

    try {
      await installPromptEvent.prompt();
      const choiceResult = await installPromptEvent.userChoice;

      if (choiceResult.outcome === "accepted") {
        setIsInstalled(true);
        setInstallPromptEvent(null);
      }
    } catch (error) {
      console.error("Install prompt failed", error);
    }
  };

  const getInstallHelpContent = () => {
    if (isAndroidChrome) {
      return {
        eyebrow: t("install.android.eyebrow"),
        title: t("install.android.title"),
        intro: t("install.android.intro"),
        steps: [
          {
            icon: "menu" as const,
            title: t("install.android.steps.menu.title"),
            detail: t("install.android.steps.menu.detail"),
          },
          {
            icon: "install" as const,
            title: t("install.android.steps.install.title"),
            detail: t("install.android.steps.install.detail"),
          },
          {
            icon: "home" as const,
            title: t("install.android.steps.confirm.title"),
            detail: t("install.android.steps.confirm.detail"),
          },
        ],
        tip: t("install.android.tip"),
      };
    }

    if (isIosSafari) {
      return {
        eyebrow: t("install.ios.eyebrow"),
        title: t("install.ios.title"),
        intro: t("install.ios.intro"),
        steps: [
          {
            icon: "share" as const,
            title: t("install.ios.steps.share.title"),
            detail: t("install.ios.steps.share.detail"),
          },
          {
            icon: "install" as const,
            title: t("install.ios.steps.install.title"),
            detail: t("install.ios.steps.install.detail"),
          },
          {
            icon: "home" as const,
            title: t("install.ios.steps.confirm.title"),
            detail: t("install.ios.steps.confirm.detail"),
          },
        ],
        tip: t("install.ios.tip"),
      };
    }

    return {
      eyebrow: t("install.generic.eyebrow"),
      title: t("install.generic.title"),
      intro: t("install.generic.intro"),
      steps: [
        {
          icon: "menu" as const,
          title: t("install.generic.steps.menu.title"),
          detail: t("install.generic.steps.menu.detail"),
        },
        {
          icon: "install" as const,
          title: t("install.generic.steps.install.title"),
          detail: t("install.generic.steps.install.detail"),
        },
        {
          icon: "home" as const,
          title: t("install.generic.steps.confirm.title"),
          detail: t("install.generic.steps.confirm.detail"),
        },
      ],
      tip: t("install.generic.tip"),
    };
  };

  const renderActionButtons = (mode: "desktop" | "mobile") => {
    const isMobile = mode === "mobile";
    const showInstallButton =
      !isInstalled && (!!installPromptEvent || isMobileInstallContext);
    const certificateButtonClassName = isMobile
      ? "mobile-nav-item mobile-nav-button"
      : undefined;
    const askHelpButtonClassName = isMobile
      ? "mobile-nav-item mobile-nav-button"
      : undefined;

    return (
      <div className={`action-buttons action-buttons--${mode}`}>
        {showInstallButton ? (
          <div
            className={`action-button-wrapper ${
              isMobile ? "action-button-wrapper--mobile" : ""
            }`}
          >
            <PLButton
              variant="blue"
              size="lg"
              className={
                isMobile
                  ? "mobile-nav-item mobile-nav-button"
                  : "install-app-button"
              }
              onClick={() => {
                if (isMobile) {
                  closeMobileMenu();
                }
                void installApp();
              }}
            >
              {t("install.action")}
            </PLButton>
          </div>
        ) : null}
        <div
          className={`action-button-wrapper ${
            isMobile ? "action-button-wrapper--mobile" : ""
          }`}
          onClick={isMobile ? closeMobileMenu : undefined}
        >
          {isMobile ? (
            <AskHelp buttonClassName={askHelpButtonClassName} />
          ) : (
            <CertificateDownload
              user={user}
              userData={userData}
              buttonClassName={certificateButtonClassName}
            />
          )}
        </div>
        <div
          className={`action-button-wrapper ${
            isMobile ? "action-button-wrapper--mobile" : ""
          }`}
          onClick={isMobile ? closeMobileMenu : undefined}
        >
          {isMobile ? (
            <CertificateDownload
              user={user}
              userData={userData}
              buttonClassName={certificateButtonClassName}
            />
          ) : (
            <AskHelp buttonClassName={askHelpButtonClassName} />
          )}
        </div>
      </div>
    );
  };

  const renderNotification = () => {
    if (
      course?.data &&
      course?.data.finish &&
      course?.certificates &&
      course?.certificates.length
    ) {
      // Safely get the certificate with the latest comp_date
      if (!course.certificates || course.certificates.length === 0) {
        return null;
      }
      const latestCert = course.certificates.reduce((latest, current) => {
        return new Date(current.comp_date) > new Date(latest.comp_date)
          ? current
          : latest;
      });
      const currentCourseVersion = course?.data?.version || "2.0.0";
      const latestCertificateVersion = latestCert?.version || null;
      const needsRecertificationForVersion =
        !latestCertificateVersion ||
        latestCertificateVersion !== currentCourseVersion;

      if (needsRecertificationForVersion) {
        return (
          <div className="notification_container">
            {t("certification.newVersion")}{" "}
            <PLButton size="lg" onClick={recertify}>
              {t("certification.recertify")}
            </PLButton>
          </div>
        );
      }

      // Then get its comp_date
      const completed_date = latestCert.comp_date;
      const msBetweenDates = Math.abs(
        new Date(completed_date).getTime() - new Date().getTime()
      );
      const daysBetweenDates = msBetweenDates / (24 * 60 * 60 * 1000);
      switch (true) {
        case daysBetweenDates >= 335:
          return (
            <div className="notification_container">
              {t(
                daysBetweenDates >= 365
                  ? "certification.expired"
                  : "certification.expiring",
              )}{" "}
              <PLButton size="lg" onClick={recertify}>
                {t("certification.recertify")}
              </PLButton>
            </div>
          );
        default:
          return null;
      }
    }
  };

  const installHelpContent = getInstallHelpContent();

  return (
    <header className="navbar-container">
      <Modal
        opened={showInstallHelp}
        onClose={() => setShowInstallHelp(false)}
        title={t("install.action")}
        closeButtonLabel={t("accessibility.closeInstall")}
        centered
        classNames={{
          body: "install-help-modal__body",
          title: "install-help-modal__title",
        }}
      >
        <Stack spacing="md" className="install-help-modal">
          <div className="install-help-modal__hero">
            <span className="install-help-modal__eyebrow">
              {installHelpContent.eyebrow}
            </span>
            <Text className="install-help-modal__headline">
              {installHelpContent.title}
            </Text>
            <Text className="install-help-modal__intro">
              {installHelpContent.intro}
            </Text>
          </div>
          <div className="install-help-modal__steps">
            {installHelpContent.steps.map((step, index) => (
              <div className="install-help-modal__step" key={step.title}>
                <div className="install-help-modal__step-number">
                  {index + 1}
                </div>
                <div className="install-help-modal__step-content">
                  <div className="install-help-modal__step-header">
                    <div className="install-help-modal__step-icon">
                      <InstallIcon type={step.icon} />
                    </div>
                    <Text className="install-help-modal__step-title">
                      {step.title}
                    </Text>
                  </div>
                  <Text className="install-help-modal__step-detail">
                    {step.detail}
                  </Text>
                </div>
              </div>
            ))}
          </div>
          <div className="install-help-modal__tip">
            <Text className="install-help-modal__tip-label">
              {t("install.helpfulTip")}
            </Text>
            <Text className="install-help-modal__tip-text">
              {installHelpContent.tip}
            </Text>
          </div>
        </Stack>
      </Modal>
      <Box
        component="nav"
        aria-label={t("accessibility.primaryNavigation")}
        className="co-nav"
        sx={(theme) => ({
          backgroundColor: theme.colors.dark[9],
          paddingLeft: theme.spacing.xl,
          paddingRight: theme.spacing.xl,
          [theme.fn.smallerThan("sm")]: {
            paddingLeft: theme.spacing.md,
            paddingRight: theme.spacing.md,
          },
        })}
      >
        <div className="bar-container">
          <div
            className="co-nav__brand"
          >
            <a
              href="https://coachsafely.org/"
              className="co-nav__brand--text"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={logo}
                alt="CoachSafely logo"
                className="co-nav__brand--img"
              />
            </a>
            <div className="navbar-right-section">
              <LanguageSwitcher />
              {user?.email && (
                <div className="user-info">
                  <span className="user-email">{user?.email}</span>
                  <UserDiv
                    user={user}
                    isAdmin={userData?.is_admin}
                    userData={userData}
                    disabled={accountDisabled}
                  />
                </div>
              )}
            </div>
            <button
              type="button"
              className={`mobile-menu-toggle ${
                mobileMenuOpen ? "mobile-menu-toggle--open" : ""
              }`}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-nav-menu"
              aria-label={t(
                mobileMenuOpen ? "navigation.closeMenu" : "navigation.openMenu",
              )}
              onClick={() => setMobileMenuOpen((previous) => !previous)}
            >
              <svg
                className="mobile-menu-toggle__icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {mobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>
      </Box>
      <nav
        id="mobile-nav-menu"
        aria-label={t("accessibility.mobileNavigation")}
        hidden={!mobileMenuOpen}
        className={`mobile-menu-panel ${
          mobileMenuOpen ? "mobile-menu-panel--open" : ""
        }`}
      >
        <div className="mobile-menu-content">
          <div className="mobile-language-switcher">
            <LanguageSwitcher />
          </div>
          {user?.email ? (
            <div className="mobile-menu-profile">
              <span className="mobile-menu-profile__label">
                {t("navigation.signedInAs")}
              </span>
              <span className="mobile-menu-profile__email">{user.email}</span>
            </div>
          ) : null}
          {showMobilePageLinks ? (
            <div className="mobile-account-actions">
              <button
                type="button"
                className={`mobile-nav-item mobile-nav-button ${
                  location.pathname === "/account"
                    ? "mobile-nav-item--active"
                    : ""
                }`}
                onClick={goToAccount}
                disabled={accountDisabled}
                aria-current={location.pathname === "/account" ? "page" : undefined}
              >
                {t("navigation.accountSettings")}
              </button>
              <button
                type="button"
                className={`mobile-nav-item mobile-nav-button ${
                  location.pathname === "/course"
                    ? "mobile-nav-item--active"
                    : ""
                }`}
                onClick={() => goToCourseSection("/course")}
                aria-current={location.pathname === "/course" ? "page" : undefined}
              >
                {t("navigation.course")}
              </button>
              <button
                type="button"
                className={`mobile-nav-item mobile-nav-button ${
                  location.pathname === "/background"
                    ? "mobile-nav-item--active"
                    : ""
                }`}
                onClick={() => goToCourseSection("/background")}
                aria-current={location.pathname === "/background" ? "page" : undefined}
              >
                {t("navigation.backgroundCheck")}
              </button>
            </div>
          ) : null}
          {renderActionButtons("mobile")}
          {user?.email ? (
            <div className="mobile-account-actions mobile-account-actions--final">
              <PLButton
                variant="primary"
                size="lg"
                className="mobile-nav-login-button mobile-nav-button"
                onClick={signOut}
              >
                {t("navigation.signOut")}
              </PLButton>
            </div>
          ) : null}
        </div>
      </nav>
      <div className="bg-image-container">
        <img
          src={bgImg}
          alt={t("navigation.certificationsAlt")}
          className="bg-image"
        />
        <div className="help-button-overlay">
          <div className="desktop-overlay-content">
            {showDesktopPageLinks ? (
              <nav
                className="course-background-tabs"
                aria-label={t("accessibility.courseNavigation")}
              >
                <button
                  type="button"
                  className={`course-background-tabs__item ${
                    location.pathname === "/course"
                      ? "course-background-tabs__item--active"
                      : ""
                  }`}
                  onClick={() => goToCourseSection("/course")}
                  aria-current={location.pathname === "/course" ? "page" : undefined}
                >
                  {t("navigation.course")}
                </button>
                <button
                  type="button"
                  className={`course-background-tabs__item ${
                    location.pathname === "/background"
                      ? "course-background-tabs__item--active"
                      : ""
                  }`}
                  onClick={() => goToCourseSection("/background")}
                  aria-current={location.pathname === "/background" ? "page" : undefined}
                >
                  {t("navigation.backgroundCheck")}
                </button>
              </nav>
            ) : null}
            {renderActionButtons("desktop")}
          </div>
        </div>
      </div>
      {renderNotification()}
    </header>
  );
};

export default NavbarComponent;
